import React, { useState } from "react";
import { useSelector } from "react-redux";
import useApi from "../../../CustomHooks/useApi";
import useRDService from "../../../CustomHooks/useRDService";
import { Fingerprint, ArrowLeft } from "lucide-react";
import $ from "jquery";

export default function AadhaarPayTwoFactorAuth({ onBack, onSuccess }) {
    const merchantdetails = useSelector((state) => state.auth?.employeeDetails);
    const userId = merchantdetails?.username;

    const { lat, lng } = useSelector((state) => state.location);

    const { captureFingerprint, loading: rdLoading } = useRDService();

    // ── APIs (Aadhaar Pay specific) ──
    const { callApi: dailylogin } = useApi("/aeps/fingpay/daily-2fa?serviceType=AP", "POST");
    // const { callApi: dailylogin } = useApi(`/aeps/fingpay/daily-2fa-status/${userId}/AP`, "Get");
    const { callApi: initiateMinKyc } = useApi("/aeps/fingpay/signup/min-kyc", "POST");

    const [captureLoading, setCaptureLoading] = useState(false);
    const [retryLoading, setRetryLoading]     = useState(false);
    const [captureMessage, setCaptureMessage] = useState("");
    const [retryError, setRetryError]         = useState("");
    const [showGeoForm, setShowGeoForm]       = useState(false);

    const [geoForm, setGeoForm] = useState({
        name:    merchantdetails?.name || "",
        mobile:  merchantdetails?.mobile_number || "",
        email:   merchantdetails?.email || "",
        pan:     merchantdetails?.pan || "",
        aadhaar: merchantdetails?.aadhaar_number || "",
        dob:     merchantdetails?.dob || "",
        gender:  merchantdetails?.gender || "",
        address: merchantdetails?.address?.full || "",
        city:    merchantdetails?.address?.city || "",
        pincode: merchantdetails?.address?.pincode || "",
    });

    const formatDOB = (dob) => {
        if (!dob) return "";
        const d = new Date(dob);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    };

    const handleGeoChange = (e) =>
        setGeoForm({ ...geoForm, [e.target.name]: e.target.value });

    // ── PID XML Parser ──
    const parsePidXml = (xml) => {
        const $xml = $(xml);
        const get = (sel, attr) => $xml.find(sel).attr(attr) || "0";
        const infoVal = (name) =>
            $xml.find(`Info[name='${name}']`).attr("value") ||
            $xml.find(`Param[name='${name}']`).attr("value") ||
            "0";

        return {
            dc:          get("DeviceInfo", "dc"),
            ci:          get("Skey", "ci"),
            hmac:        $xml.find("Hmac").text() || "0",
            dpId:        get("DeviceInfo", "dpId"),
            mc:          get("DeviceInfo", "mc"),
            pidDataType: get("Data", "type"),
            sessionKey:  $xml.find("Skey").text() || "0",
            mi:          get("DeviceInfo", "mi"),
            rdsId:       get("DeviceInfo", "rdsId"),
            errCode:     get("Resp", "errCode"),
            errInfo:     get("Resp", "errInfo"),
            fCount:      get("Resp", "fCount"),
            fType:       get("Resp", "fType"),
            iCount:      get("Resp", "iCount"),
            iType:       get("Resp", "iType"),
            pCount:      get("Resp", "pCount"),
            pType:       "0",
            srno:        infoVal("srno"),
            sysid:       infoVal("sysid"),
            ts:          infoVal("ts"),
            pidData:     $xml.find("Data").text() || "0",
            qScore:      get("Resp", "qScore"),
            nmPoints:    get("Resp", "nmPoints"),
            rdsVer:      get("DeviceInfo", "rdsVer"),
        };
    };

    // ── Daily 2FA Login ──
    const handle2FALogin = async () => {
        try {
            setCaptureLoading(true);
            setCaptureMessage("Place finger on scanner...");
            setRetryError("");

            const pidXml = await captureFingerprint("");
            if (!pidXml || !pidXml.includes('errCode="0"')) {
                setCaptureMessage("Fingerprint capture failed");
                return;
            }

            const pidData = parsePidXml(pidXml);
            const response = await dailylogin({
                mid: userId,
                latitude:  lat?.toString(),
                longitude: lng?.toString(),
                biometricData: pidData,
            });

            if (response?.statuscode === "TXN") {
                setCaptureMessage("✅ Daily 2FA Login Successful");
                if (onSuccess) setTimeout(() => onSuccess(), 1200);
            } else if (
                response?.statuscode === "ERR" &&
                response?.status?.includes("Geo-fencing authentication failed")
            ) {
                setCaptureMessage(response.status);
                setShowGeoForm(true);
            } else {
                setCaptureMessage(
                    response?.status || response?.message || "Authentication failed"
                );
            }
        } catch (err) {
            setCaptureMessage(err?.message || "Something went wrong");
        } finally {
            setCaptureLoading(false);
        }
    };

    // ── Geo-fencing fallback ──
    const retryGeoKyc = async () => {
        try {
            setRetryLoading(true);
            setRetryError("");

            const body = {
                mobile:      geoForm.mobile,
                name:        geoForm.name,
                gender:      geoForm.gender,
                pan:         geoForm.pan.toUpperCase(),
                email:       geoForm.email,
                aadhaar:     geoForm.aadhaar,
                dateOfBirth: formatDOB(geoForm.dob),
                latitude:    lat?.toString(),
                longitude:   lng?.toString(),
                address: {
                    full:    geoForm.address,
                    city:    geoForm.city,
                    pincode: geoForm.pincode,
                },
            };

            const res = await initiateMinKyc(body, `?userId=${userId}`);

            if (res?.statuscode === "TXN") {
                setRetryError("");
                setShowGeoForm(false);
                setCaptureMessage("Min KYC completed. Now retry 2FA.");
                return;
            }
            setRetryError(res?.message || "Retry failed");
        } catch {
            setRetryError("Something went wrong");
        } finally {
            setRetryLoading(false);
        }
    };

    return (
        <div className="bg-[#F5F7FB] px-6 py-10">
            <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-start justify-center gap-8">

                {/* LEFT — Capture card */}
                <div className="w-full lg:w-[420px] sticky top-10">
                    <div className="bg-white rounded-3xl shadow-xl p-8">

                        <button
                            onClick={onBack}
                            className="flex items-center gap-2 mb-8 text-gray-600 hover:text-blue-600"
                        >
                            <ArrowLeft size={18} /> Back
                        </button>

                        <div className="w-24 h-24 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-6">
                            <Fingerprint size={50} className="text-blue-600" />
                        </div>

                        <h2 className="text-2xl font-semibold text-center mb-3">
                            Daily 2FA Authentication
                        </h2>
                        <p className="text-gray-500 text-center mb-8">
                            Capture fingerprint for Aadhaar Pay outlet login
                        </p>

                        <button
                            onClick={handle2FALogin}
                            disabled={captureLoading || rdLoading}
                            className="w-full bg-blue-600 text-white py-4 rounded-xl hover:bg-blue-700 font-medium disabled:opacity-50"
                        >
                            {captureLoading || rdLoading ? "Capture Processing..." : "Capture Fingerprint"}
                        </button>

                        {captureMessage && (
                            <div className="mt-5 p-4 rounded-xl bg-blue-50 border text-sm text-center">
                                {captureMessage}
                            </div>
                        )}
                    </div>
                </div>

                {/* RIGHT — Geo form (fallback) */}
                {showGeoForm && (
                    <div className="flex-1 w-full">
                        <div className="bg-white rounded-3xl shadow-xl p-8 border">
                            <div className="mb-6">
                                <h3 className="text-2xl font-semibold text-red-600 mb-2">Geo Fencing Failed</h3>
                                <p className="text-gray-500">Update details and retry E-KYC</p>
                            </div>

                            <div className="grid md:grid-cols-2 gap-5">
                                <GeoInput label="Name"    name="name"    value={geoForm.name}    onChange={handleGeoChange} />
                                <GeoInput label="Mobile"  name="mobile"  value={geoForm.mobile}  onChange={handleGeoChange} />
                                <GeoInput label="Email"   name="email"   value={geoForm.email}   onChange={handleGeoChange} />
                                <GeoInput label="PAN"     name="pan"     value={geoForm.pan}     onChange={handleGeoChange} />
                                <GeoInput label="Aadhaar" name="aadhaar" value={geoForm.aadhaar} onChange={handleGeoChange} />
                                <GeoInput label="DOB" type="date" name="dob" value={geoForm.dob} onChange={handleGeoChange} />

                                <div>
                                    <label className="block mb-2 font-medium">Gender</label>
                                    <select
                                        name="gender"
                                        value={geoForm.gender}
                                        onChange={handleGeoChange}
                                        className="w-full border rounded-xl p-3"
                                    >
                                        <option value="">Select</option>
                                        <option value="M">Male</option>
                                        <option value="F">Female</option>
                                        <option value="T">Transgender</option>
                                    </select>
                                </div>

                                <GeoInput label="Address" name="address" value={geoForm.address} onChange={handleGeoChange} />
                                <GeoInput label="City"    name="city"    value={geoForm.city}    onChange={handleGeoChange} />
                                <GeoInput label="Pincode" name="pincode" value={geoForm.pincode} onChange={handleGeoChange} />
                            </div>

                            <button
                                onClick={retryGeoKyc}
                                disabled={retryLoading}
                                className="w-full mt-8 bg-blue-600 text-white py-4 rounded-xl hover:bg-blue-700 font-semibold disabled:opacity-50"
                            >
                                {retryLoading ? "Retrying E-KYC..." : "Retry E-KYC"}
                            </button>

                            {retryError && (
                                <div className="mt-4 p-4 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm text-center">
                                    {retryError}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function GeoInput({ label, ...props }) {
    return (
        <div>
            <label className="block mb-1">{label}</label>
            <input {...props} className="w-full border rounded-lg p-3" />
        </div>
    );
}
